import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { useNavigate } from "react-router";
import { Search20Regular, ArrowEnter20Regular } from "@fluentui/react-icons";
import { useCommandPalette } from "../../context/CommandPaletteContext";
import { useAuth } from "../../context/AuthContext";
import { useDebouncedValue } from "../../utils/useDebouncedValue";
import { renderSnippet } from "../../utils/renderSnippet";
import { globalSearch, type SearchHit, type SearchHitType } from "../../services/globalSearchService";
import { kontaktAnlagePfad, useKontaktAnlageTyp } from "../../utils/kontaktAnlageTypen";
import { anlagePfadAusSuche } from "../../utils/nameAusSuche";

const UI = "'Hanken Grotesk', 'Segoe UI', Tahoma, sans-serif";
const PLUM = "#3b2240";
const ACCENT = "#ffa714";

const TYPE_META: Record<SearchHitType, { label: string; route: string }> = {
  contact: { label: "Kontakte", route: "/contacts" },
  contract: { label: "Verträge", route: "/contracts" },
  risk: { label: "Risiken", route: "/risks" },
  damage: { label: "Schäden", route: "/schaeden" },
  vorgang: { label: "Vorgänge", route: "/vorgaenge" },
  mail: { label: "Schriftverkehr", route: "/emails" },
  termin: { label: "Termine", route: "/termine" },
  document: { label: "Dokumente", route: "/contacts" }, // route ungenutzt — Backend setzt hit.route bei Dokumenten immer
};

// Schnelle DB-Domänen vs. langsame Graph-Domänen (Mail/Termine) — getrennt
// abgefragt, damit die DB-Treffer sofort erscheinen und Graph nachfüllt.
const DB_TYPES: SearchHitType[] = ["contact", "contract", "risk", "damage", "vorgang", "document"];
const GRAPH_TYPES: SearchHitType[] = ["mail", "termin"];

interface Cmd {
  label: string;
  to: string;
  perm?: string;
  group: "Navigation" | "Anlegen";
}

const COMMANDS: Cmd[] = [
  { label: "Dashboard öffnen", to: "/", group: "Navigation" },
  { label: "Kontakte öffnen", to: "/contacts", perm: "contacts:read", group: "Navigation" },
  { label: "CO intern öffnen", to: "/co-intern", perm: "cointern:read", group: "Navigation" },
  { label: "Risiken öffnen", to: "/risks", perm: "risks:read", group: "Navigation" },
  { label: "Verträge öffnen", to: "/contracts", perm: "contracts:read", group: "Navigation" },
  { label: "Schäden öffnen", to: "/damages", perm: "damages:read", group: "Navigation" },
  { label: "Vorgänge öffnen", to: "/vorgaenge", perm: "vorgaenge:read", group: "Navigation" },
  { label: "Termine öffnen", to: "/termine", group: "Navigation" },
  { label: "Schriftverkehr öffnen", to: "/emails", group: "Navigation" },
  { label: "Telefon öffnen", to: "/anrufe", perm: "calls:read", group: "Navigation" },
  { label: "Einstellungen öffnen", to: "/settings", group: "Navigation" },
  { label: "Neuer Kontakt", to: "/contacts/new", perm: "contacts:create", group: "Anlegen" },
  { label: "Neues Risiko", to: "/risks/new", perm: "risks:create", group: "Anlegen" },
  { label: "Neuer Termin", to: "/termine", group: "Anlegen" },
];

interface Item {
  key: string;
  group: string;
  title: string;
  subtitle?: string;
  /** Fundstellen-Snippet (Dokument-Treffer) — gerendert via renderSnippet. */
  snippet?: string;
  run: () => void;
  /** Eingerückte Detail-Zeile (aufgeklappter Mail-/Termin-Treffer). */
  indent?: boolean;
  /** Kategorie-Zeile (Schriftverkehr/Termine) mit Auf-/Zuklapp-Indikator. */
  chevron?: "open" | "closed";
}

export const CommandPalette: React.FC = () => {
  const { open, setOpen } = useCommandPalette();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const anlageTyp = useKontaktAnlageTyp();
  const canCreateContact = hasPermission("contacts:create");
  const [query, setQuery] = useState("");
  const [dbHits, setDbHits] = useState<SearchHit[]>([]);
  // Suchbegriff, zu dem `dbHits` gehören — erst wenn er zum aktuellen Begriff
  // passt, ist die DB-Suche „fertig" (sonst stünden alte Treffer für einen
  // neuen Begriff).
  const [dbTerm, setDbTerm] = useState("");
  const [graphHits, setGraphHits] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [graphLoading, setGraphLoading] = useState(false);
  const [expandMail, setExpandMail] = useState(false);
  const [expandTermin, setExpandTermin] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);
  const debounced = useDebouncedValue(query, 250);

  // Beim Öffnen zurücksetzen + Fokus.
  useEffect(() => {
    if (open) {
      setQuery("");
      setDbHits([]);
      setDbTerm("");
      setGraphHits([]);
      setExpandMail(false);
      setExpandTermin(false);
      setActive(0);
      const t = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  // Live-Suche (debounced, abbrechbar) — DB-Domänen schnell; Graph (Mail/Termine)
  // getrennt parallel, damit die DB-Treffer sofort erscheinen und Mail/Termine
  // nachfüllen, sobald Graph antwortet.
  useEffect(() => {
    if (!open) return;
    const term = debounced.trim();
    if (term.length < 2) {
      setDbHits([]);
      setDbTerm("");
      setGraphHits([]);
      setLoading(false);
      setGraphLoading(false);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    setGraphLoading(true);
    globalSearch(term, ctrl.signal, DB_TYPES)
      .then((r) => {
        setDbHits(r);
        setDbTerm(term);
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
    globalSearch(term, ctrl.signal, GRAPH_TYPES)
      .then((r) => setGraphHits(r))
      .catch(() => undefined)
      .finally(() => setGraphLoading(false));
    return () => ctrl.abort();
  }, [debounced, open]);

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  // „Verein, Stiftung …" steht nicht in COMMANDS: ob es den Einstieg gibt, sagt
  // der Rechtsform-Katalog (utils/kontaktAnlageTypen.ts).
  const cmds = useMemo(() => {
    const q = query.trim().toLowerCase();
    const alle: Cmd[] = [
      ...COMMANDS,
      ...(anlageTyp
        ? [
            {
              label: `Neuer Kontakt (${anlageTyp.label})`,
              to: kontaktAnlagePfad(anlageTyp),
              perm: "contacts:create",
              group: "Anlegen" as const,
            },
          ]
        : []),
    ];
    return alle.filter(
      (c) => (!c.perm || hasPermission(c.perm)) && (q === "" || c.label.toLowerCase().includes(q)),
    );
  }, [query, hasPermission, anlageTyp]);

  const items = useMemo<Item[]>(() => {
    const out: Item[] = [];
    // Befehle (Navigation / Anlegen)
    cmds.forEach((c) =>
      out.push({ key: `cmd:${c.group}:${c.to}:${c.label}`, group: c.group, title: c.label, run: () => go(c.to) }),
    );
    // DB-Treffer (geclustert nach Typ): Kontakte, Verträge, Risiken, Schäden, Vorgänge, Dokumente
    dbHits.forEach((h) =>
      out.push({
        key: `hit:${h.type}:${h.id}`,
        group: TYPE_META[h.type].label,
        title: h.title,
        subtitle: h.subtitle,
        snippet: h.snippet,
        run: () => go(h.route ?? `${TYPE_META[h.type].route}/${h.id}`),
      }),
    );
    // „Kontakt nicht gefunden – neu anlegen?": Sobald die DB-Suche zum aktuellen
    // Begriff fertig ist und KEIN Kontakt dabei ist — auch wenn Verträge, Mails
    // oder Befehle getroffen wurden. Nur mit dem Recht zur Kontaktanlage.
    const term = query.trim();
    if (
      canCreateContact &&
      term.length >= 2 &&
      !loading &&
      dbTerm === term &&
      !dbHits.some((h) => h.type === "contact")
    ) {
      out.push({
        key: "neu:firma",
        group: "Kontakt nicht gefunden",
        title: `„${term}“ als Firma neu anlegen`,
        run: () => go(anlagePfadAusSuche("firma", term)),
      });
      out.push({
        key: "neu:privat",
        group: "Kontakt nicht gefunden",
        title: `„${term}“ als Privatkontakt neu anlegen`,
        run: () => go(anlagePfadAusSuche("privat", term)),
      });
    }
    // Mail + Termine als eigene, aufklappbare Punkte. Bei 0 DB-Treffern automatisch
    // aufgeklappt (dann zeigen wir direkt die Details).
    const noDb = dbHits.length === 0;
    const addCategory = (type: "mail" | "termin", expanded: boolean, toggle: () => void) => {
      const hs = graphHits.filter((h) => h.type === type);
      if (!hs.length && !graphLoading) return;
      const count = graphLoading && !hs.length ? "…" : String(hs.length);
      out.push({
        key: `cat:${type}`,
        group: "Mehr",
        title: `${TYPE_META[type].label} (${count} Treffer)`,
        run: toggle,
        chevron: expanded ? "open" : "closed",
      });
      if (expanded) {
        hs.forEach((h) =>
          out.push({
            key: h.route ? `hit:${h.route}` : `hit:${h.type}:${h.id}`,
            group: "Mehr",
            title: h.title,
            subtitle: h.subtitle,
            indent: true,
            run: () => go(h.route ?? `${TYPE_META[h.type].route}/${h.id}`),
          }),
        );
      }
    };
    addCategory("mail", expandMail || noDb, () => setExpandMail((v) => !v));
    addCategory("termin", expandTermin || noDb, () => setExpandTermin((v) => !v));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmds, dbHits, dbTerm, query, loading, canCreateContact, graphHits, graphLoading, expandMail, expandTermin]);

  // Aktiven Index in Reichweite halten + ins Sichtfeld scrollen.
  useEffect(() => {
    setActive((a) => (items.length === 0 ? 0 : Math.min(a, items.length - 1)));
  }, [items.length]);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [active]);

  if (!open) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, Math.max(0, items.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      items[active]?.run();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  let lastGroup = "";

  return createPortal(
    <div
      role="presentation"
      onClick={() => setOpen(false)}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 6000,
        background: "rgba(40,24,45,0.42)",
        backdropFilter: "blur(2px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        paddingTop: "12vh",
      }}
    >
      <div
        role="dialog"
        aria-label="Globale Suche"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "92vw",
          maxWidth: 620,
          maxHeight: "64vh",
          display: "flex",
          flexDirection: "column",
          background: "#ffffff",
          borderRadius: 14,
          boxShadow: "0 30px 80px -20px rgba(40,24,45,0.55)",
          overflow: "hidden",
          fontFamily: UI,
        }}
      >
        {/* Eingabe */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: "1px solid #eee4ee" }}>
          <span style={{ color: "#9a8a9c", display: "flex" }}>
            <Search20Regular />
          </span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Suchen oder Befehl wählen…"
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              fontSize: 17,
              fontFamily: UI,
              color: "#241522",
              background: "transparent",
            }}
          />
          {(loading || graphLoading) && <span style={{ fontSize: 12, color: "#9a8a9c" }}>sucht…</span>}
        </div>

        {/* Ergebnisliste */}
        <div style={{ overflowY: "auto", padding: "6px 0" }}>
          {items.length === 0 ? (
            <div style={{ padding: "22px 16px", color: "#9a8a9c", fontSize: 14, textAlign: "center" }}>
              {query.trim().length < 2 ? "Tippe, um Kontakte, Verträge, Vorgänge … zu finden." : "Keine Treffer."}
            </div>
          ) : (
            items.map((it, i) => {
              const showHeader = it.group !== lastGroup;
              lastGroup = it.group;
              const isActive = i === active;
              return (
                <React.Fragment key={it.key}>
                  {showHeader && (
                    <div
                      style={{
                        padding: "10px 18px 4px",
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: "#9a8a9c",
                      }}
                    >
                      {it.group}
                    </div>
                  )}
                  <button
                    ref={isActive ? activeRef : undefined}
                    type="button"
                    onMouseEnter={() => setActive(i)}
                    onClick={() => it.run()}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      width: "100%",
                      textAlign: "left",
                      border: "none",
                      cursor: "pointer",
                      padding: it.indent ? "8px 16px 8px 40px" : "9px 16px",
                      background: isActive ? "rgba(255,167,20,0.14)" : "transparent",
                      boxShadow: isActive ? `inset 3px 0 0 ${ACCENT}` : "none",
                      fontFamily: UI,
                    }}
                  >
                    {it.chevron && (
                      <span style={{ color: "#9a8a9c", fontSize: 12, width: 12, display: "inline-flex", justifyContent: "center", flexShrink: 0 }} aria-hidden>
                        {it.chevron === "open" ? "▾" : "▸"}
                      </span>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14.5, fontWeight: it.chevron ? 700 : 600, color: PLUM, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {it.title}
                      </div>
                      {it.subtitle && (
                        <div style={{ fontSize: 12.5, color: "#6f5c70", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {it.subtitle}
                        </div>
                      )}
                      {it.snippet && (
                        <div style={{ fontSize: 12, color: "#8a7a8b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {renderSnippet(it.snippet)}
                        </div>
                      )}
                    </div>
                    {isActive && (
                      <span style={{ color: "#9a8a9c", display: "flex" }} aria-hidden>
                        <ArrowEnter20Regular />
                      </span>
                    )}
                  </button>
                </React.Fragment>
              );
            })
          )}
        </div>

        {/* Fußzeile mit Tastatur-Hinweisen */}
        <div style={{ display: "flex", gap: 16, padding: "8px 16px", borderTop: "1px solid #eee4ee", fontSize: 12, color: "#9a8a9c" }}>
          <span>↑↓ navigieren</span>
          <span>↵ öffnen</span>
          <span>esc schließen</span>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CommandPalette;
